import React, { useEffect, useMemo, useState } from "react";
import { Tabs, Radio, Button, Space, Typography, Card, Empty, Input } from "antd";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import RecipientCard from "./RecipientCard";
import { useApiWithFilters } from "../../../../hooks/search/useApiWithFilters";
import { useManagers } from "../hook/useManagers";
import LoaderTsx from "../../../modules/Loader.tsx";
import { useSendDocumentForSignature } from "../hook/useSendDocumentForSignature";

const { Text } = Typography;

/* -------------------- Types -------------------- */

type Recipient = {
  id: string;
  name: string;
  email: string;
};

type SendForSignatureProps = {
  setSendForSignature?: (value: boolean) => void;
  setModalWidth?: (value: number) => void;
  setCustomTitle?: (value: string | null) => void;
  documentName?: string;
};

/* -------------------- Styles -------------------- */

const scrollContainerStyle: React.CSSProperties = {
  width: "100%",
  maxHeight: 250,
  minHeight: 250,
  overflowY: "auto",
  paddingRight: 16,
};

/* -------------------- Component -------------------- */

const SendForSignature: React.FC<SendForSignatureProps> = ({
  setSendForSignature,
  setModalWidth,
  setCustomTitle,
  documentName,
}) => {
  const [activeTab, setActiveTab] = useState<"owner" | "other">("owner");
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selectedRecipientId, setSelectedRecipientId] = useState<string>();
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(null);
  const [searchText, setSearchText] = useState("");

  const { bookingDetails } = useSelector((state: any) => state.newBooking);
  const { leaseId = bookingDetails?.leaseId || "" } = useParams();

  /* -------------------- DocuSign State -------------------- */

  const sentDocs = bookingDetails?.docuSign || [];
  const alreadySentRecipient = sentDocs.length > 0 ? sentDocs[0] : null;

  /* -------------------- APIs -------------------- */

  const {
    ownerOptions,
    loading: ownerLoading,
    data,
    fetchData,
  } = useApiWithFilters({
    apiUrl: "/unit/owner-details/" + bookingDetails?.unitId,
    method: "GET",

    // initialParams: { limit: 20000, page: 1 },
  });

  const { options, loading: managerLoading, fetchManagers } = useManagers();

  const { sendDocument, loading: sendLoading } = useSendDocumentForSignature(onCancel);

  /* -------------------- Effects -------------------- */

  useEffect(() => {
    const loadRecipients = async () => {
      if (activeTab === "owner") {
        await fetchData({
          singleOnwer :true,
          // propertyId: bookingDetails?.propertyId,
          unitId: bookingDetails?.unitId,
        });
        setRecipients(ownerOptions || []);
      } else {
        await fetchManagers();
        setRecipients(options || []);
      }

      setSearchText("");
      setSelectedRecipient(null);
      setSelectedRecipientId(undefined);
    };

    loadRecipients();
  }, [activeTab, ownerOptions?.length]);

  // Auto-select already-sent recipient

  const rensedToRecipient = async () => {
    await sendDocument({
      documentName: "TENANCY_CONTRACT",
      leaseId,
      recipientEmail: alreadySentRecipient?.recipientEmail,
      recipientName: alreadySentRecipient?.recipientName,
      sendTo: "landlord",
    });
  };

  /* -------------------- Search -------------------- */

  const filteredRecipients = useMemo(() => {
    if (!searchText) return recipients;

    const search = searchText.toLowerCase();
    return recipients?.filter(
      (r) => r?.name?.toLowerCase()?.includes(search) || r?.email?.toLowerCase()?.includes(search)
    );
  }, [recipients, searchText]);

  /* -------------------- Handlers -------------------- */

  function onCancel() {
    setSendForSignature?.(false);
    setCustomTitle?.(null);
    setModalWidth?.(850);
  }

  const handleSelect = (id: string) => {
    if (alreadySentRecipient) return;

    const recipient = recipients.find((r) => r.id === id) || null;
    setSelectedRecipient(recipient);
    setSelectedRecipientId(id);
  };

  const handleSend = async () => {
    if (!selectedRecipient) return;

    await sendDocument({
      documentName: "TENANCY_CONTRACT",
      leaseId,
      recipientEmail: selectedRecipient.email,
      recipientName: selectedRecipient.name,
      sendTo: "landlord",
    });
  };

  /* -------------------- Render -------------------- */

  if (ownerLoading || managerLoading || sendLoading) {
    return <LoaderTsx height="39vh" />;
  }

  return (
    <>
      {!alreadySentRecipient && <Text strong>Select a recipient to send the Contract for signature.</Text>}

      {alreadySentRecipient && (
        <Text strong>This document was already sent. You can re-send it to the same recipient.</Text>
      )}

      {alreadySentRecipient ? (
        <RecipientCard
          id={alreadySentRecipient?.leaseId}
          name={alreadySentRecipient?.recipientName}
          email={alreadySentRecipient.recipientEmail}
          isLocked={false}
          selectedId={selectedRecipientId}
          handleSelect={handleSelect}
          reSend={true}
        />
      ) : (
        <>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as "owner" | "other")}
            style={{ marginTop: 16 }}
            items={[
              { key: "owner", label: "Owner / Landlord" },
              { key: "other", label: "Other Signatory" },
            ]}
          />

          <Input
            placeholder="Search by name or email"
            allowClear
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ marginBottom: 16, maxWidth: 660 }}
          />

          <Radio.Group value={selectedRecipientId} style={{ width: "100%" }}>
            <Space
              direction="vertical"
              style={filteredRecipients.length > 2 ? scrollContainerStyle : { width: "100%" }}
            >
              {filteredRecipients.length === 0 ? (
                <Empty description="No recipients found" />
              ) : (
                filteredRecipients.map((item) => {
                  return (
                    <RecipientCard
                      id={item.id}
                      name={item.name}
                      email={item.email}
                      isLocked={alreadySentRecipient && item.email !== alreadySentRecipient.recipientEmail}
                      selectedId={selectedRecipientId}
                      handleSelect={handleSelect}
                    />
                  );
                })
              )}
            </Space>
          </Radio.Group>
        </>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 30,
          gap: 8,
        }}
      >
        <Button className="ef-discard-btn" onClick={onCancel}>
          Cancel
        </Button>

        <Button
          type="primary"
          className="docu-sign-btn-blue"
          disabled={!selectedRecipientId && !alreadySentRecipient}
          onClick={alreadySentRecipient ? rensedToRecipient : handleSend}
        >
          {alreadySentRecipient ? "Resend for Signature" : "Send for Signature"}
        </Button>
      </div>
    </>
  );
};

export default SendForSignature;
